/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import type Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';

export enum HandlingEventType {
  Resume = 'resume',
  Pause = 'pause',
}

export enum PauseReason {
  ConversationSwitch = 'conversation_switch',
}

export interface HandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: Date;
  reason?: PauseReason;
}

export default class ConversationHandlingService extends Service {
  @service declare session: Session;

  @tracked private activeConversationId: number | null = null;

  private shouldTrackConversation(conversation: Conversation): boolean {
    let isOpen = conversation.state === ConversationState.Open;
    let isAssignedToMe = conversation.adminAssignee?.id === this.session.teammate.id;

    return isOpen && isAssignedToMe;
  }

  private createResumeEvent(conversationId: number): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Resume,
      timestamp: new Date(),
    };

    // eslint-disable-next-line no-console
    console.log('[AHT] Resume event created', {
      conversationId,
      timestamp: event.timestamp.toISOString(),
      adminId: this.session.teammate.id,
    });

    this.activeConversationId = conversationId;
  }

  private createPauseEvent(conversationId: number, reason?: PauseReason): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Pause,
      timestamp: new Date(),
      reason,
    };

    // eslint-disable-next-line no-console
    console.log('[AHT] Pause event created', {
      conversationId,
      timestamp: event.timestamp.toISOString(),
      reason: reason || 'none',
      adminId: this.session.teammate.id,
    });

    if (this.activeConversationId === conversationId) {
      this.activeConversationId = null;
    }
  }

  resumeConversation(conversation: Conversation): void {
    // Pause the currently active conversation if switching to a different one
    if (this.activeConversationId && this.activeConversationId !== conversation.id) {
      this.createPauseEvent(this.activeConversationId, PauseReason.ConversationSwitch);
    }

    // Only resume if conversation is trackable (open + assigned to current teammate)
    if (!this.shouldTrackConversation(conversation)) {
      // eslint-disable-next-line no-console
      console.log('[AHT] Conversation not trackable - skipping resume', {
        conversationId: conversation.id,
        state: conversation.state,
        assignedToMe: conversation.adminAssignee?.id === this.session.teammate.id,
      });
      return;
    }

    // Don't create duplicate resume if already active
    if (this.activeConversationId === conversation.id) {
      return;
    }

    this.createResumeEvent(conversation.id);
  }
}

declare module '@ember/service' {
  interface Registry {
    'conversation-handling-service': ConversationHandlingService;
  }
}
